import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  DesignV2Scope,
  MobilePage,
  ProgressIndicator,
  StickyBottomActions,
} from "../../design-v2";
import {
  getTrainingProfileOptions,
  getUserSettings,
  updateTrainingProfileSettings,
  updateUserOnboarding,
  updateUserProfile,
} from "../../services/api";
import {
  createOnboardingDraft,
  mergeOnboardingStepIntoCanonical,
  restoreOnboardingSession,
} from "./onboardingDraft";
import { isOnboardingFrontendEnabled } from "./featureFlags";
import { isOnboardingComplete } from "./OnboardingGate";
import useAuthBootstrap from "../../auth/useAuthBootstrap";
import {
  validateAboutYou,
  validateFinalStep,
  hasValidOnboardingCardioRole,
  validateMuscleStep,
  validateSetupStep,
  validateTrainingStep,
} from "./onboardingValidation";
import {
  getCycleBuilderPath,
  getCycleDetailsPath,
} from "../multiWeek/routes";
import { toTrainingProfilePayload } from "../settings/settingsMappers";
import AboutYouStep from "./steps/AboutYouStep";
import AdditionalContextStep from "./steps/AdditionalContextStep";
import MuscleFocusStep from "./steps/MuscleFocusStep";
import ProfileSummaryPanel from "./ProfileSummaryPanel";
import OnboardingConflictModal from "./OnboardingConflictModal";
import {
  GeneratedProgramResult,
  GenerationProgress,
} from "../../components/product";
import TrainingSetupStep from "./steps/TrainingSetupStep";
import TrainingStep from "./steps/TrainingStep";
import { buildProfileSummaryItems } from "./profileSummary";
import useOnboardingGeneration, {
  createGenerationId,
} from "./useOnboardingGeneration";
import { resolvePreferredTrainingDays } from "./trainingDayDefaults";
import {
  createAnonymousOnboardingDraft,
  commitAnonymousOnboardingDraftMutation,
  deriveAnonymousCurrentStep,
  loadAnonymousOnboardingDraft,
} from "./onboardingDraftStore";
import "./onboarding.css";

const TOTAL_STEPS = 5;
function resetOnboardingScroll(mainElement) {
  let element = mainElement;
  while (element) {
    element.scrollTop = 0;
    element.scrollLeft = 0;
    element = element.parentElement;
  }
  if (document.scrollingElement) {
    document.scrollingElement.scrollTop = 0;
    document.scrollingElement.scrollLeft = 0;
  }
  window.scrollTo?.({ top: 0, left: 0, behavior: "auto" });
}

function parseRequestedStep(searchParams) {
  const value = Number(searchParams.get("step"));
  return Number.isInteger(value) && value >= 1 && value <= TOTAL_STEPS
    ? value
    : null;
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const bootstrap = useAuthBootstrap();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loadState, setLoadState] = useState({ status: "loading", error: "" });
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [anonymousRecord, setAnonymousRecord] = useState(null);
  const [profile, setProfile] = useState(null);
  const [step, setStep] = useState(1);
  const [lastCompletedStep, setLastCompletedStep] = useState(0);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveError, setSaveError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [hasMovementLimitations, setHasMovementLimitations] = useState(false);
  const pageMainRef = useRef(null);
  const mountedRef = useRef(true);
  const previousStepRef = useRef(null);
  const headerContentRef = useRef(null);
  const enabled = isOnboardingFrontendEnabled();
  const isAuthenticated = bootstrap.status === "authenticated";
  const { applyOnboarding } = bootstrap;
  const availabilityOptions = settings?.trainingProfile?.options?.availability ||
    settings?.availability || {
    sessionsPerWeek: [],
    durationPerSession: [],
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const {
    beginProgramGeneration,
    generationProgress,
    handleConflictCancel,
    handleConflictConfirm,
    handleProgramRetry,
    programFlow,
    restorePendingGeneration,
  } = useOnboardingGeneration({
    applyOnboarding,
    draft,
    loadStatus: loadState.status,
    mountedRef,
    navigate,
    saveFinalTrainingProfile,
  });

  const initialize = useCallback(async () => {
    setLoadState({ status: "loading", error: "" });
    try {
      if (!isAuthenticated) {
        const storedDraft = loadAnonymousOnboardingDraft();
        if (storedDraft?.lastCompletedStep === 5 && storedDraft.pendingGeneration) {
          navigate("/auth", { replace: true });
          return;
        }
        const options = await getTrainingProfileOptions();
        const record = storedDraft || createAnonymousOnboardingDraft({
          answers: toTrainingProfilePayload(createOnboardingDraft({})),
          identity: { displayName: "", age: "", sex: "" },
        });
        const anonymousDraft = createOnboardingDraft({
          trainingProfile: { profile: record.answers },
        });
        const anonymousProfile = {
          ...record.identity,
          demographicsStatus: "NOT_COLLECTED",
        };
        const resumeStep = deriveAnonymousCurrentStep(record.lastCompletedStep);

        setSettings({ availability: options });
        setAnonymousRecord(record);
        setDraft(anonymousDraft);
        setProfile(anonymousProfile);
        setStep(resumeStep);
        setLastCompletedStep(record.lastCompletedStep);
        setHasMovementLimitations(
          (anonymousDraft.movementConstraints?.painIssues || []).length > 0 ||
            (anonymousDraft.movementConstraints?.manualBlockedExerciseIds || []).length > 0
        );
        navigate(`/onboarding?step=${resumeStep}`, { replace: true });
        setLoadState({ status: "loaded", error: "" });
        return;
      }

      const currentSettings = await getUserSettings();

      if (isOnboardingComplete(currentSettings)) {
        // A route guard sent us here from a stale bootstrap snapshot. Reconcile
        // it before leaving: otherwise that same guard immediately bounces the
        // user back and the two redirects oscillate until the browser refuses
        // any further history.replaceState calls.
        applyOnboarding?.(currentSettings?.meta?.onboarding);
        navigate("/", { replace: true });
        return;
      }

      const session = restoreOnboardingSession(currentSettings);
      const beginResponse = await updateUserOnboarding({ action: "BEGIN" });
      const begunOnboarding = beginResponse?.onboarding || currentSettings.meta?.onboarding;
      const begunLastCompletedStep = Number(
        Math.max(
          session.lastCompletedStep,
          Number(begunOnboarding?.lastCompletedStep || 0)
        )
      );
      const resumeStep = Math.min(
        TOTAL_STEPS,
        Math.max(session.step, begunLastCompletedStep + 1)
      );

      setSettings(currentSettings);
      setAnonymousRecord(null);
      setDraft(session.draft);
      setProfile(session.profile);
      setStep(resumeStep);
      setLastCompletedStep(begunLastCompletedStep);
      setHasMovementLimitations(
        (session.draft?.movementConstraints?.painIssues || []).length > 0 ||
          (session.draft?.movementConstraints?.manualBlockedExerciseIds || []).length > 0
      );
      const pendingGeneration = bootstrap.pendingGeneration;
      if (pendingGeneration) {
        restorePendingGeneration(pendingGeneration, {
          trainingDays: resolvePreferredTrainingDays(
            session.draft?.availability?.preferredTrainingDays,
            session.draft?.availability?.sessionsPerWeek
          ),
        });
      }
      navigate(`/onboarding?step=${resumeStep}`, { replace: true });
      setLoadState({ status: "loaded", error: "" });
    } catch (error) {
      setLoadState({
        status: "error",
        error: error?.message || "Unable to start onboarding.",
      });
    }
  }, [
    applyOnboarding,
    bootstrap.pendingGeneration,
    isAuthenticated,
    navigate,
    restorePendingGeneration,
  ]);

  useEffect(() => {
    if (enabled) {
      initialize();
    }
  }, [enabled, initialize]);

  useLayoutEffect(() => {
    if (programFlow.phase === "success") {
      resetOnboardingScroll(pageMainRef.current);
    }
  }, [programFlow.phase]);

  // The onboarding header is sticky, so anything that sticks beneath it needs
  // its real height rather than a guessed constant (safe-area insets and text
  // scaling both move it). Published as a custom property the stylesheet reads.
  useLayoutEffect(() => {
    const headerElement = headerContentRef.current?.closest(
      ".lz-v2-mobile-page__header"
    );
    const pageElement = headerElement?.parentElement;
    if (!headerElement || !pageElement) {
      return undefined;
    }

    const publishHeaderHeight = () => {
      pageElement.style.setProperty(
        "--lz-onboarding-header-height",
        `${headerElement.getBoundingClientRect().height}px`
      );
    };
    publishHeaderHeight();

    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(publishHeaderHeight);
    observer.observe(headerElement);
    return () => observer.disconnect();
  }, [loadState.status, programFlow.phase, step]);

  // Every real step transition starts at the top. The previous step is tracked
  // in a ref rather than derived from render state so that the first loaded
  // render (a fresh visit or a restored draft resuming mid-flow) records the
  // step without scrolling, and rerenders within a step never scroll at all.
  useLayoutEffect(() => {
    if (loadState.status !== "loaded") {
      return;
    }
    if (previousStepRef.current === null) {
      previousStepRef.current = step;
      return;
    }
    if (previousStepRef.current === step) {
      return;
    }
    previousStepRef.current = step;
    resetOnboardingScroll(pageMainRef.current);
  }, [loadState.status, step]);

  useEffect(() => {
    if (loadState.status !== "loaded") {
      return;
    }

    const requestedStep = parseRequestedStep(searchParams);
    const maximumStep = Math.min(TOTAL_STEPS, lastCompletedStep + 1);
    if (requestedStep && requestedStep <= maximumStep) {
      if (requestedStep !== step) {
        setStep(requestedStep);
        setFieldErrors({});
        setSaveError("");
      }
      return;
    }

    setSearchParams({ step: String(step) }, { replace: true });
  }, [lastCompletedStep, loadState.status, searchParams, setSearchParams, step]);

  if (!enabled) {
    return <Navigate to="/" replace />;
  }

  function updateDraft(nextDraft) {
    setDraft({ ...nextDraft, primaryGoal: "HYPERTROPHY" });
    setFieldErrors({});
    setSaveError("");
  }

  function updateProfile(nextProfile) {
    setProfile(nextProfile);
    setFieldErrors({});
    setSaveError("");
  }

  function moveToStep(nextStep, completedStep) {
    const nextLastCompletedStep = Math.max(lastCompletedStep, completedStep);
    setLastCompletedStep(nextLastCompletedStep);
    setStep(nextStep);
    setSearchParams({ step: String(nextStep) });
    setFieldErrors({});
    setSaveError("");
  }

  async function saveAboutYou() {
    const validation = validateAboutYou({
      profile,
      experience: draft.experience,
    });
    if (!validation.ok) {
      setFieldErrors(validation.fieldErrors);
      return;
    }

    const profilePayload = {
      displayName: profile.displayName,
    };
    if (profile.demographicsStatus !== "LOCKED") {
      profilePayload.age = Number(profile.age);
      profilePayload.sex = profile.sex;
    }

    const response = await updateUserProfile(profilePayload);
    const savedProfile = response.profile || {};
    const nextProfile = {
      ...profile,
      ...savedProfile,
      displayName: savedProfile.displayName || profile.displayName.trim(),
      age: savedProfile.currentAge ?? savedProfile.age ?? profile.age,
      sex: savedProfile.sex || profile.sex,
      demographicsStatus:
        savedProfile.demographicsStatus || profile.demographicsStatus,
    };
    const lifecycleResponse = await updateUserOnboarding({
      action: "ADVANCE",
      lastCompletedStep: 1,
    });

    setProfile(nextProfile);
    moveToStep(2, lifecycleResponse?.onboarding?.lastCompletedStep ?? 1);
  }

  async function saveCanonicalStep(completedStep) {
    let draftToSave = draft;
    if (lastCompletedStep >= 2) {
      const latestSettings = await getUserSettings();
      draftToSave = mergeOnboardingStepIntoCanonical(
        createOnboardingDraft(latestSettings),
        draft,
        completedStep
      );
    }
    const payload = toTrainingProfilePayload(draftToSave);
    const response = await updateTrainingProfileSettings(payload);
    const authoritativeDraft = createOnboardingDraft(response);
    const lifecycleResponse = await updateUserOnboarding({
      action: "ADVANCE",
      lastCompletedStep: completedStep,
    });

    setSettings(response);
    setDraft(authoritativeDraft);
    moveToStep(
      Math.min(TOTAL_STEPS, completedStep + 1),
      lifecycleResponse?.onboarding?.lastCompletedStep ?? completedStep
    );
  }

  async function saveFinalTrainingProfile() {
    const latestSettings = await getUserSettings();
    const draftToSave = mergeOnboardingStepIntoCanonical(
      createOnboardingDraft(latestSettings),
      draft,
      5
    );
    const payload = toTrainingProfilePayload(draftToSave);
    const response = await updateTrainingProfileSettings(payload);
    const authoritativeDraft = createOnboardingDraft(response);

    if (mountedRef.current) {
      setSettings(response);
      setDraft(authoritativeDraft);
    }
    return response;
  }

  function saveAnonymousStep(completedStep) {
    const nextRecord = commitAnonymousOnboardingDraftMutation(anonymousRecord, {
      answers: toTrainingProfilePayload(draft),
      identity: {
        displayName: profile.displayName.trim(),
        age: Number(profile.age),
        sex: profile.sex,
      },
      completedStep,
    });
    if (!nextRecord) {
      throw new Error("Unable to save this onboarding draft on this device.");
    }
    setAnonymousRecord(nextRecord);
    setDraft(createOnboardingDraft({
      trainingProfile: { profile: nextRecord.answers },
    }));
    setProfile({
      ...nextRecord.identity,
      demographicsStatus: "NOT_COLLECTED",
    });
    // When a more-advanced stored snapshot wins a cross-tab race the whole
    // draft is replaced, so this flag has to be re-derived from that winner
    // exactly as initialize() does. Leaving it behind would show "No movement
    // limitations" over a draft that actually carries pain issues.
    setHasMovementLimitations(
      (nextRecord.answers?.movementConstraints?.painIssues || []).length > 0 ||
        (nextRecord.answers?.movementConstraints?.manualBlockedExerciseIds || [])
          .length > 0
    );
    if (nextRecord.lastCompletedStep === 5 && nextRecord.pendingGeneration) {
      navigate("/auth");
      return nextRecord;
    }
    moveToStep(
      deriveAnonymousCurrentStep(nextRecord.lastCompletedStep),
      nextRecord.lastCompletedStep
    );
    return nextRecord;
  }

  function sealAnonymousGeneration() {
    const generationId = anonymousRecord?.pendingGeneration?.generationId ||
      createGenerationId();
    const startedAt = anonymousRecord?.pendingGeneration?.startedAt ||
      new Date().toISOString();
    const trainingDays = resolvePreferredTrainingDays(
      draft.availability?.preferredTrainingDays,
      draft.availability?.sessionsPerWeek
    );
    const sealedRecord = commitAnonymousOnboardingDraftMutation(anonymousRecord, {
      answers: toTrainingProfilePayload(draft),
      identity: {
        displayName: profile.displayName.trim(),
        age: Number(profile.age),
        sex: profile.sex,
      },
      completedStep: TOTAL_STEPS,
      pendingGeneration: { generationId, startedAt, trainingDays },
    });
    if (!sealedRecord) {
      throw new Error("Unable to seal this onboarding draft on this device.");
    }
    setAnonymousRecord(sealedRecord);
    navigate("/auth");
  }

  async function handleContinue() {
    if (isSaving) {
      return;
    }

    let validation;
    if (step === 1) {
      validation = validateAboutYou({ profile, experience: draft.experience });
    } else if (step === 2) {
      validation = validateTrainingStep(draft, availabilityOptions);
    } else if (step === 3) {
      validation = validateMuscleStep(draft, availabilityOptions);
    } else if (step === 4) {
      validation = validateSetupStep(draft);
    } else {
      validation = validateFinalStep(
        draft,
        availabilityOptions,
        hasMovementLimitations
      );
    }

    if (!validation.ok) {
      setFieldErrors(validation.fieldErrors || {});
      setSaveError((validation.formErrors || [])[0] || "");
      return;
    }

    setIsSaving(true);
    setFieldErrors({});
    setSaveError("");
    try {
      if (!isAuthenticated) {
        if (step === TOTAL_STEPS) {
          sealAnonymousGeneration();
        } else {
          saveAnonymousStep(step);
        }
      } else if (step === 1) {
        await saveAboutYou();
      } else if (step < TOTAL_STEPS) {
        await saveCanonicalStep(step);
      } else {
        await beginProgramGeneration();
      }
    } catch (error) {
      if (!mountedRef.current) {
        return;
      }
      const nextErrors = {};
      (Array.isArray(error?.details) ? error.details : []).forEach((issue) => {
        if (issue?.path && !nextErrors[issue.path]) {
          nextErrors[issue.path] = issue.message;
        }
      });
      setFieldErrors(nextErrors);
      setSaveError(error?.message || "Unable to save. Your changes are still here.");
    } finally {
      if (mountedRef.current) {
        setIsSaving(false);
      }
    }
  }

  function handleBack() {
    if (step <= 1 || isSaving || programFlow.phase !== "step") {
      return;
    }
    const previousStep = step - 1;
    setStep(previousStep);
    setSearchParams({ step: String(previousStep) });
    setFieldErrors({});
    setSaveError("");
  }

  function renderStep() {
    if (step === 1) {
      return (
        <AboutYouStep
          profile={profile}
          experience={draft.experience}
          onProfileChange={updateProfile}
          onExperienceChange={(experience) => updateDraft({ ...draft, experience })}
          fieldErrors={fieldErrors}
          disabled={isSaving}
        />
      );
    }
    if (step === 2) {
      return (
        <TrainingStep
          draft={draft}
          availabilityOptions={availabilityOptions}
          onChange={updateDraft}
          fieldErrors={fieldErrors}
          disabled={isSaving}
        />
      );
    }
    if (step === 3) {
      return (
        <MuscleFocusStep
          draft={draft}
          availabilityOptions={availabilityOptions}
          onChange={updateDraft}
          fieldErrors={fieldErrors}
          disabled={isSaving}
        />
      );
    }
    if (step === 4) {
      return (
        <TrainingSetupStep
          draft={draft}
          onChange={updateDraft}
          fieldErrors={fieldErrors}
          disabled={isSaving}
        />
      );
    }
    return (
      <AdditionalContextStep
        draft={draft}
        onChange={updateDraft}
        hasMovementLimitations={hasMovementLimitations}
        onMovementLimitationsChange={setHasMovementLimitations}
        fieldErrors={fieldErrors}
        disabled={isSaving}
        allowMovementAnalysis={isAuthenticated}
      />
    );
  }

  function renderProgramFlow() {
    if (["checking", "generating", "converting", "completing"].includes(programFlow.phase)) {
      return (
        <GenerationProgress
          stage={programFlow.phase}
          percent={generationProgress.percent}
          message={generationProgress.message}
          profile={draft}
          isExiting={generationProgress.isCompletionExiting}
        />
      );
    }

    if (programFlow.phase === "success") {
      const cycleId = programFlow.cycle?.cycleId || programFlow.cycle?.cycle?.id;
      return (
        <GeneratedProgramResult
          mode="onboarding"
          weeklyPlan={programFlow.weeklyPlan}
          cycle={programFlow.cycle}
          profile={draft}
          onModify={() => navigate(getCycleBuilderPath(cycleId))}
          onDetails={() => navigate(getCycleDetailsPath(cycleId))}
        />
      );
    }

    if (programFlow.phase === "error") {
      return (
        <section className="mx-auto grid max-w-md gap-4 py-20 text-center">
          <h1 className="font-lz-v2-display text-2xl font-bold text-lz-v2-text-strong">
            We couldn&apos;t finish your program
          </h1>
          <p className="text-sm leading-6 text-lz-v2-danger" role="alert">
            {programFlow.error}
          </p>
          <Button onClick={handleProgramRetry}>Try again</Button>
        </section>
      );
    }

    return (
      <>
        {renderStep()}
        {saveError ? (
          <p className="mt-6 text-sm font-semibold text-lz-v2-danger" role="alert">
            {saveError}
          </p>
        ) : null}
      </>
    );
  }

  return (
    <DesignV2Scope>
      <MobilePage
        className={`lz-onboarding-page${step === TOTAL_STEPS ? " lz-onboarding-page--final" : ""}${["checking", "generating", "converting", "completing"].includes(programFlow.phase) ? " lz-onboarding-page--generation" : ""}${programFlow.phase === "success" ? " lz-onboarding-page--result" : ""}`}
        hasStickyActions={
          loadState.status === "loaded" &&
          (programFlow.phase === "step" || programFlow.phase === "success")
        }
        mainProps={{ ref: pageMainRef }}
        header={
          programFlow.phase === "step" ? <>
            <ProgressIndicator
              className="lz-onboarding-progress-top"
              value={step}
              min={0}
              max={TOTAL_STEPS}
              label="Onboarding progress"
            />
            <div
              className="mx-auto w-full max-w-[640px] px-5 py-4 sm:px-12"
              ref={headerContentRef}
            >
              <div className="flex items-center justify-between gap-4">
                <button
                  type="button"
                  className="min-h-11 min-w-11 rounded-full text-left text-sm font-bold text-lz-v2-text-muted disabled:opacity-0"
                  onClick={handleBack}
                  disabled={step <= 1 || isSaving || programFlow.phase !== "step"}
                  aria-label="Go to previous onboarding step"
                >
                  Back
                </button>
                <p className="font-lz-v2-display text-lg font-bold uppercase text-lz-v2-text-strong">
                  Lyft <span className="text-lz-v2-action">Zone</span>
                </p>
                {isAuthenticated ? (
                  <span className="w-11 text-right text-xs font-bold text-lz-v2-text-muted">
                    {step}/{TOTAL_STEPS}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="min-h-11 min-w-11 text-right text-sm font-bold text-lz-v2-action"
                    onClick={() => navigate("/login")}
                  >
                    Log in
                  </button>
                )}
              </div>
            </div>
          </> : (
            <div className="lz-onboarding-brand-header">
              <p className="font-lz-v2-display text-lg font-bold uppercase text-lz-v2-text-strong">
                Lyft <span className="text-lz-v2-action">Zone</span>
              </p>
            </div>
          )
        }
      >
        {loadState.status === "error" ? (
          <div className="mx-auto grid max-w-md gap-4 py-20 text-center">
            <h1 className="font-lz-v2-display text-2xl font-bold text-lz-v2-text-strong">
              We couldn&apos;t start onboarding
            </h1>
            <p className="text-lz-v2-text-muted" role="alert">{loadState.error}</p>
            <Button onClick={initialize}>Try again</Button>
          </div>
        ) : loadState.status === "loaded" && draft && profile ? (
          renderProgramFlow()
        ) : (
          <p className="py-20 text-center text-lz-v2-text-muted" role="status">
            Preparing your profile…
          </p>
        )}
      </MobilePage>

      {loadState.status === "loaded" && programFlow.phase === "step" ? (
        <StickyBottomActions
          aria-label="Onboarding actions"
          className={step === TOTAL_STEPS ? "lz-onboarding-final-actions" : ""}
          innerClassName={step === TOTAL_STEPS ? "lz-onboarding-final-actions__inner" : ""}
        >
          {step === TOTAL_STEPS && draft ? (
            <ProfileSummaryPanel items={buildProfileSummaryItems(draft)} />
          ) : null}
          <Button
            size="lg"
            className="lz-onboarding-continue"
            disabled={step === TOTAL_STEPS && !hasValidOnboardingCardioRole(draft)}
            isLoading={isSaving}
            loadingLabel={step === TOTAL_STEPS ? "Preparing..." : "Saving..."}
            onClick={handleContinue}
          >
            {step === TOTAL_STEPS ? "Generate my program" : "Continue"}
          </Button>
        </StickyBottomActions>
      ) : null}

      {loadState.status === "loaded" && programFlow.phase === "confirmation" ? (
        <OnboardingConflictModal
          conflicts={programFlow.conflicts}
          onCancel={handleConflictCancel}
          onConfirm={handleConflictConfirm}
          isBusy={isSaving}
        />
      ) : null}
    </DesignV2Scope>
  );
}
